import { Truck, Tag, Ticket, Layers, Scaling, RotateCcw, Clock, LayoutTemplate } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

// The eight recommendation rules, in the order the engine applies them. The
// sidebar, the rules overview and each rule's own page all read this list, so
// a rule added here appears everywhere at once and "1 of 8 rules live" in the
// nav can never disagree with the page it points at.
export const SECTIONS = [
  { key: 'shipping', n: 1, title: 'Free shipping', icon: Truck, why: 'The most reliable basket lever there is. The cart knows its total; you know the threshold.' },
  { key: 'pairs', n: 2, title: 'Pairs and discounts', icon: Tag, why: 'A pair your own buyers already choose, with a reason to take it now.' },
  { key: 'codes', n: 3, title: 'Coupon codes', icon: Ticket, why: 'A code on every pick, applied at checkout after the add.' },
  { key: 'sets', n: 4, title: 'Complete the set', icon: Layers, why: 'Three to six products as one card and one click.' },
  { key: 'sizes', n: 5, title: 'Bigger size, better price', icon: Scaling, why: 'On staples, the bigger pack argued on unit price. No extra decision for the shopper.' },
  { key: 'reorder', n: 6, title: 'Time to reorder', icon: RotateCcw, why: 'A known customer, past the usual gap, gets the one card that says so.' },
  { key: 'dispatch', n: 7, title: 'Shipping promise', icon: Clock, why: '"Order in 2h 10m, ships today" is a reason to add now, and only ever true.' },
  { key: 'surfaces', n: 8, title: 'Where it shows', icon: LayoutTemplate, why: 'Cart page, cart drawer, product page. Each one is a choice.' },
];

export const rulePath = (key) => `/commerce/recommendations/rules/${key}`;

// Mirrors the engine's isLive: a rule counts only inside its validity window.
export const isLive = (o) => !!o && o.active !== false && !(o.starts_at && new Date(o.starts_at) > new Date()) && !(o.ends_at && new Date(o.ends_at) < new Date());

/** Which of the eight rules are live, from the saved config and the reorder rules. */
export function liveMap(cfg, reorderRules) {
  if (!cfg) return null;
  const offers = cfg.offers || [];
  return {
    shipping: !!cfg.free_shipping_threshold || (cfg.unlocks || []).some(isLive),
    pairs: offers.some((o) => o.type === 'pair' && isLive(o)),
    codes: offers.some((o) => o.type === 'code' && isLive(o)),
    sets: offers.some((o) => o.type === 'set' && isLive(o)),
    sizes: (cfg.size_pairs || []).some((p) => p.active),
    reorder: (reorderRules?.rules || []).length > 0,
    dispatch: !!cfg.dispatch?.enabled,
    surfaces: true,
  };
}

/** The config, the reorder rules and the live map - same query keys as the rules page, so one fetch serves both. */
export function useRecsRules({ enabled = true } = {}) {
  const { data: cfg } = useQuery({ queryKey: ['recs', 'config'], queryFn: async () => (await api.get('/api/commerce/recs-config')).data, enabled });
  const { data: reorderRules } = useQuery({ queryKey: ['recs', 'reorder-rules'], queryFn: async () => (await api.get('/api/commerce/recovery/replenishment-rules')).data, enabled });
  const live = liveMap(cfg, reorderRules);
  const liveCount = live ? SECTIONS.filter((s) => live[s.key]).length : null;
  return { cfg, reorderRules, live, liveCount, total: SECTIONS.length };
}
